import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from './Header.module.css';
import cloutLogo from '../../assets/logos/clout-logo.png';

interface HeaderProps {
  onSignInClick?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onSignInClick }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();

  // Handle scroll for header styling
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMenuOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const handleNavClick = useCallback((action: () => void) => {
    setIsMenuOpen(false);
    action();
  }, []);

  const navItems = [
    { label: 'Find a Provider', onClick: () => navigate('/search') },
    { label: 'Who We Are', onClick: () => navigate('/who-we-are') },
    { label: 'Get Certified', onClick: () => navigate('/wizard') },
    { label: 'Sign In', onClick: onSignInClick || (() => navigate('/signin')), isPrimary: true },
  ];

  return (
    <>
      <header
        className={`${styles.glassMorphismHeader} ${isScrolled ? styles.scrolled : ''}`}
        data-name="Header"
        id="node-12_9969"
      >
        <div className={styles.headerContent}>
          {/* Logo */}
          <Link to="/" className={styles.logoContainer}>
            <img
              src={cloutLogo}
              alt="CLOUT - Healthcare Provider Review Platform"
              className={styles.logoImage}
              loading="eager"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                console.warn('Failed to load CLOUT logo');
              }}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className={styles.desktopNav} aria-label="Main navigation">
            {navItems.map((item) => (
              <button
                key={item.label}
                onClick={item.onClick}
                className={`${styles.navLink} ${item.isPrimary ? styles.navLinkPrimary : ''}`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className={styles.menuButton}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu"
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <span className={`${styles.menuIcon} ${isMenuOpen ? styles.menuIconOpen : ''}`}>
              <span className={styles.menuBar}></span>
              <span className={styles.menuBar}></span>
              <span className={styles.menuBar}></span>
            </span>
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`${styles.mobileMenuOverlay} ${isMenuOpen ? styles.mobileMenuOpen : ''}`}
        onClick={() => setIsMenuOpen(false)}
        aria-hidden={!isMenuOpen}
      />

      {/* Mobile Menu */}
      <nav
        id="mobile-menu"
        className={`${styles.mobileMenu} ${isMenuOpen ? styles.mobileMenuOpen : ''}`}
        aria-label="Mobile navigation"
        aria-hidden={!isMenuOpen}
      >
        <div className={styles.mobileMenuContent}>
          {navItems.map((item, index) => (
            <button
              key={item.label}
              onClick={() => handleNavClick(item.onClick || (() => {}))}
              className={`${styles.mobileNavLink} ${item.isPrimary ? styles.mobileNavLinkPrimary : ''}`}
              style={{ animationDelay: `${index * 50}ms` }}
              tabIndex={isMenuOpen ? 0 : -1}
            >
              {item.label}
            </button>
          ))}
        </div>
      </nav>
    </>
  );
};

export default Header;
